<template>
    <AppLayout>
        <Head title="Statistiques des messages" />

        <div class="px-4 sm:px-6 lg:px-8 py-6">
            <div class="sm:flex sm:items-center sm:justify-between mb-6">
                <div>
                    <h1 class="text-2xl font-bold text-gray-800">Statistiques des messages</h1>
                    <p class="mt-1 text-sm text-gray-500">Analyse des performances des messages de l'application.</p>
                </div>

                <div class="mt-4 sm:mt-0">
                    <select v-model="selectedPeriod" @change="changePeriod"
                        class="form-select rounded-md border-gray-300 text-sm shadow-sm focus:border-sky-500 focus:ring-sky-500"
                    >
                        <option value="7d">7 derniers jours</option>
                        <option value="30d">30 derniers jours</option>
                        <option value="90d">90 derniers jours</option>
                        <option value="custom">Période personnalisée</option>
                    </select>
                </div>
            </div>

            <div v-if="selectedPeriod === 'custom'" class="bg-white shadow-lg rounded-sm border border-gray-200 p-4 mb-6">
                <div class="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                    <div>
                        <JetLabel value="Date de début:" />
                        <input v-model="from" type="date"
                            class="w-full rounded-md border-gray-300 text-sm focus:border-sky-500 focus:ring-sky-500"
                        />
                    </div>

                    <div>
                        <JetLabel value="Date de fin:" />
                        <input v-model="to" type="date"
                            class="w-full rounded-md border-gray-300 text-sm focus:border-sky-500 focus:ring-sky-500"
                        />
                    </div>

                    <div>
                        <button type="button" @click="applyCustomPeriod"
                            class="w-full md:w-auto px-5 py-2 rounded-md bg-sky-500 text-white text-sm font-medium hover:bg-sky-600 transition"
                        >Appliquer
                        </button>
                    </div>
                </div>
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
                <div class="p-4 bg-white shadow-lg rounded-sm border border-gray-200">
                    <header class="flex justify-between items-center mb-3">
                        <div class="w-10 h-10 rounded-full flex items-center justify-center bg-sky-100">
                            <svg class="w-5 h-5 text-sky-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M8 10h.01M12 10h.01M16 10h.01M9 16h6m2 4H7a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v12a2 2 0 01-2 2z"
                                />
                            </svg>
                        </div>
                        <span class="text-xs text-gray-400">{{ periodLabel }}</span>
                    </header>
                    <h2 class="text-sm font-semibold text-gray-500">Total messages</h2>
                    <div class="text-2xl font-bold text-gray-800 mt-1">
                        {{ formatNumber(statistics.overview.total) }}
                    </div>
                    <p class="text-xs text-gray-400 mt-2">Messages créés durant la période</p>
                </div>

                <div class="p-4 bg-white shadow-lg rounded-sm border border-gray-200">
                    <header class="flex justify-between items-center mb-3">
                        <div class="w-10 h-10 rounded-full flex items-center justify-center bg-blue-100">
                            <svg class="w-5 h-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 12h14M13 6l6 6-6 6" />
                            </svg>
                        </div>
                        <span class="text-xs text-blue-500 font-medium">Livraisons</span>
                    </header>
                    <h2 class="text-sm font-semibold text-gray-500">Total livraisons</h2>
                    <div class="text-2xl font-bold text-gray-800 mt-1">{{ formatNumber(statistics.overview.delivered) }}</div>
                    <p class="text-xs text-gray-400 mt-2">Messages effectivement livrés</p>
                </div>

                <div class="p-4 bg-white shadow-lg rounded-sm border border-gray-200">
                    <header class="flex justify-between items-center mb-3">
                        <div class="w-10 h-10 rounded-full flex items-center justify-center bg-purple-100">
                            <svg class="w-5 h-5 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                />
                            </svg>
                        </div>
                        <span class="text-xs text-purple-500 font-medium">Lectures</span>
                    </header>

                    <h2 class="text-sm font-semibold text-gray-500">Total lectures</h2>
                    <div class="text-2xl font-bold text-gray-800 mt-1">{{ formatNumber(statistics.overview.total_reads) }}</div>
                    <p class="text-xs text-gray-400 mt-2">Lectures enregistrées</p>
                </div>

                <div class="p-4 bg-white shadow-lg rounded-sm border border-gray-200">
                    <header class="flex justify-between items-center mb-3">
                        <div class="w-10 h-10 rounded-full flex items-center justify-center bg-orange-100">
                            <svg class="w-5 h-5 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                                />
                            </svg>
                        </div>
                        <span class="text-xs text-orange-500 font-medium">Utilisateurs</span>
                    </header>

                    <h2 class="text-sm font-semibold text-gray-500">Utilisateurs lecteurs</h2>
                    <div class="text-2xl font-bold text-gray-800 mt-1">
                        {{ formatNumber(statistics.overview.unique_readers) }}
                    </div>
                    <p class="text-xs text-gray-400 mt-2">Utilisateurs uniques</p>
                </div>
            </div>

            <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div class="p-4 bg-white shadow-lg rounded-sm border border-gray-200">
                    <div class="text-xs text-gray-400">Messages programmés</div>
                    <div class="text-xl font-bold text-gray-800 mt-1">
                        {{ formatNumber(statistics.overview.scheduled) }}
                    </div>
                </div>

                <div class="p-4 bg-white shadow-lg rounded-sm border border-gray-200">
                    <div class="text-xs text-gray-400">Messages expirés</div>
                    <div class="text-xl font-bold text-gray-800 mt-1">
                        {{ formatNumber(statistics.overview.expired) }}
                    </div>
                </div>

                <div class="p-4 bg-white shadow-lg rounded-sm border border-gray-200">
                    <div class="text-xs text-gray-400">Affichage forcé</div>
                    <div class="text-xl font-bold text-gray-800 mt-1">
                        {{ formatNumber(statistics.overview.forced) }}
                    </div>
                </div>

                <div class="p-4 bg-white shadow-lg rounded-sm border border-gray-200">
                    <div class="text-xs text-gray-400">Taux de lecture</div>
                    <div class="text-xl font-bold text-gray-800 mt-1">
                        {{ formatPercentage(statistics.overview.read_rate) }}
                    </div>
                </div>
            </div>

            <div class="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-6">
                <div class="xl:col-span-2 bg-white shadow-lg rounded-sm border border-gray-200">
                    <div class="px-5 py-4 border-b border-gray-100">
                        <h2 class="text-lg font-semibold text-gray-700">Évolution des lectures</h2>
                        <p class="text-xs text-gray-400 mt-1">Nombre de lectures enregistrées chaque jour</p>
                    </div>

                    <div class="p-5">
                        <div v-if="statistics.reads_over_time.length" class="relative" style="height:350px">
                            <Line
                                :chart-data="readsChartData"
                                :chart-options="readsChartOptions"
                            />
                        </div>

                        <div v-else class="h-80 flex items-center justify-center text-sm text-gray-400">
                            Aucune lecture durant cette période.
                        </div>
                    </div>
                </div>
                <div class="bg-white shadow-lg rounded-sm border border-gray-200">
                    <div class="px-5 py-4 border-b border-gray-100">
                        <h2 class="text-lg font-semibold text-gray-700">Messages par type</h2>
                        <p class="text-xs text-gray-400 mt-1">Répartition des messages</p>
                    </div>

                    <div class="p-5">
                        <div v-if="statistics.by_type.length" class="relative" style="height:350px">
                            <Doughnut
                                :chart-data="typeChartData"
                                :chart-options="typeChartOptions"
                            />
                        </div>
                        <div v-else class="h-80 flex items-center justify-center text-sm text-gray-400">
                            Aucun message durant cette période.
                        </div>
                    </div>
                </div>
            </div>

            <div class="bg-white shadow-lg rounded-sm border border-gray-200">
                <div class="px-5 py-4 border-b border-gray-100">
                    <h2 class="text-lg font-semibold text-gray-700">Performance des messages</h2>
                    <p class="text-xs text-gray-400 mt-1">
                        Statistiques détaillées des messages créés durant la période.
                    </p>
                </div>

                <div x-data="handleSelect">
                    <Datatables>
                        <template #title>
                            Statistiques détaillées des messages créés durant la période.
                        </template>
            
                        <template #tableHeader>
                            <th>Message</th>
                            <th>Type</th>
                            <th>Livrés à</th>
                            <th>Lectures</th>
                            <th>Taux</th>
                            <th>Priorité</th>
                            <th>Statut</th>
                            <th>Date</th>
                        </template>
            
                        <template #tableBody>
                            <template v-if="!statistics.messages.length">
                                <tr>
                                    <td colspan="8" class="px-5 py-12 text-center">
                                        <div class="text-gray-400">
                                            <i class="fa fa-inbox text-3xl mb-3"></i>
                                            <p class="text-sm">Aucun message trouvé durant cette période.</p>
                                        </div>
                                    </td>
                                </tr>
                            </template>
                            <template v-else v-for="message in statistics.messages" :key="message.id">
                                <tr>
                                    <td>
                                        <div class="flex items-center">
                                            <div class="w-9 h-9 rounded-full flex items-center justify-center shrink-0 mr-3"
                                                :class="typeIconClass(message.type)"
                                            >
                                                <i :class="typeIcon(message.type)"></i>
                                            </div>
                                            <div class="font-medium text-gray-800">{{ message.title }}</div>
                                        </div>
                                    </td>
                                    <td><StatusBadge :label="message.type_label" :color="message.type_color" /></td>
                                    <td>
                                        <div class="font-semibold text-gray-800">{{ formatNumber(message.delivered) }}</div>
                                    </td>
                                    <td>
                                        <div class="font-semibold text-gray-800">{{ formatNumber(message.reads) }}</div>
                                    </td>
                                    <td>
                                        <span class="text-sm font-medium" :class="readRateClass(message.read_rate)">
                                            {{ formatPercentage(message.read_rate) }}
                                        </span>
                                    </td>
                                    <td>
                                        <span class="text-sm text-gray-600">{{ message.priority }}</span>
                                    </td>
                                    <td>
                                        <StatusBadge :label="message.is_active ? 'Actif' : 'Inactif'" :color="message.is_active ? 'greendack' : 'red'" />
                                    </td>
                                    <td class="px-5 py-4 text-right">
                                        <span class="text-sm text-gray-500">{{ formattedDateTime(message.created_at) }}</span>
                                    </td>
                                </tr>
                            </template>
                        </template>
                    </Datatables>
                </div>
            </div>
        </div>
    </AppLayout>
</template>


<script>
    import { defineComponent } from 'vue'
    import JetInput from '@/Jetstream/Input'
    import JetLabel from '@/Jetstream/Label'
    import {Line, Doughnut} from 'vue-chartjs'
    import { Inertia } from '@inertiajs/inertia'
    import { Head } from '@inertiajs/inertia-vue3'
    import AppLayout from '@/Layouts/AppLayout.vue'
    import Datatables from '@/Components/Datatables'
    import StatusBadge from '@/Components/StatusBadge'
    import {
        Chart as ChartJS,
        Title,
        Tooltip,
        Legend,
        ArcElement,
        CategoryScale,
        LinearScale,
        PointElement,
        LineElement,
        Filler
    } from 'chart.js'

    import resolveConfig from 'tailwindcss/resolveConfig'

    import tailwindConfig from '../../../../tailwind.config'
    
    const fullConfig = resolveConfig(tailwindConfig)

    import DateFormatMixins from '@/Mixins/DateFormatMixins'
    
    ChartJS.register(
        Title,
        Tooltip,
        Legend,
        ArcElement,
        CategoryScale,
        LinearScale,
        PointElement,
        LineElement,
        Filler
    )

    export default defineComponent({

        components: {
            Head,
            Line,
            JetInput,
            JetLabel,
            Doughnut,
            AppLayout,
            Datatables,
            StatusBadge
        },

        mixins: [DateFormatMixins],

        props: {
            statistics: {
                type: Object,
                required: true
            },

            filters: {
                type: Object,
                default: () => ({
                    period: '30d',
                    from: null,
                    to: null
                }),
            },
        },

        data() {
            return {
                selectedPeriod: this.filters.period || '30d',
                from: this.filters.from || null,
                to: this.filters.to || null
            }
        },

        computed: {
            periodLabel() {
                const labels = {
                    '7d': '7 jours',
                    '30d': '30 jours',
                    '90d': '90 jours',
                    'custom': 'Personnalisée'
                }

                return labels[this.selectedPeriod] || '30 jours'
            },

            readsChartData() {
                const rows = this.statistics.reads_over_time || []

                return {
                    labels: rows.map(row => this.formatChartDate(row.date)),

                    datasets: [
                        {
                            label: 'Lectures',
                            data: rows.map(row => Number(row.count || 0)),
                            borderColor: fullConfig.theme.colors.sky[500],
                            backgroundColor: fullConfig.theme.colors.sky[100],
                            pointBackgroundColor: fullConfig.theme.colors.sky[500],
                            pointBorderColor: fullConfig.theme.colors.white,
                            pointBorderWidth: 2,
                            pointRadius: 3,
                            pointHoverRadius: 5,
                            borderWidth: 2,
                            tension: 0.4,
                            fill: true,
                        }
                    ]
                }
            },

            readsChartOptions() {
                return {
                    responsive: true,
                    maintainAspectRatio: false,
                    interaction: {
                        intersect: false,
                        mode: 'index'
                    },

                    plugins: {
                        legend: {
                            display: false
                        },

                        tooltip: {
                            backgroundColor: fullConfig.theme.colors.gray[800],
                            padding: 10,
                            displayColors: false,
                            callbacks: {
                                label: context => {
                                    return `${this.formatNumber(context.parsed.y)} lecture${context.parsed.y > 1 ? 's' : ''}`
                                }
                            }
                        }
                    },

                    scales: {
                        y: {
                            beginAtZero: true,
                            ticks: {
                                precision: 0
                            },
                            grid: {
                                color: fullConfig.theme.colors.gray[100]
                            }
                        },
                        x: {
                            grid: {
                                display: false
                            }
                        }
                    }
                }
            },

            typeChartData() {
                const rows = this.statistics.by_type || []

                return {
                    labels: rows.map(row => row.label),
                    datasets: [
                        {
                            data: rows.map(row => Number(row.count || 0)),

                            backgroundColor: [
                                fullConfig.theme.colors.sky[500],
                                fullConfig.theme.colors.sky[300],
                                fullConfig.theme.colors.yellow[500],
                                fullConfig.theme.colors.orange[500],
                                fullConfig.theme.colors.gray[500],
                                fullConfig.theme.colors.greendack[500],
                            ],

                            hoverBackgroundColor: [
                                fullConfig.theme.colors.sky[600],
                                fullConfig.theme.colors.sky[400],
                                fullConfig.theme.colors.yellow[600],
                                fullConfig.theme.colors.orange[600],
                                fullConfig.theme.colors.gray[600],
                                fullConfig.theme.colors.greendack[600],
                            ],

                            borderColor: fullConfig.theme.colors.white,
                            borderWidth: 2
                        }
                    ]
                }
            },

            typeChartOptions() {
                return {
                    responsive: true,
                    maintainAspectRatio: false,
                    cutout: '68%',
                    plugins: {
                        legend: {
                            position: 'bottom',
                            labels: {
                                usePointStyle: true,
                                padding: 15
                            }
                        },

                        tooltip: {
                            callbacks: {
                                label: context => {
                                    const value = context.parsed

                                    const total = context.dataset.data.reduce(
                                        (sum, item) => sum + Number(item || 0),
                                        0
                                    )

                                    const percentage = total
                                        ? ((value / total) * 100).toFixed(1)
                                        : 0

                                    return `${context.label}: ${this.formatNumber(value)} (${percentage}%)`
                                }
                            }
                        }
                    }
                }
            },
        },

        methods: {
            changePeriod() {
                if (this.selectedPeriod === 'custom') {
                    return
                }

                Inertia.get(route('app-messages.statistics'), {period: this.selectedPeriod}, {
                        preserveState: true,
                        preserveScroll: true,
                        replace: true
                    }
                )
            },

            applyCustomPeriod() {
                if (!this.from || !this.to) {
                    return
                }

                Inertia.get(route('app-messages.statistics'), {period: 'custom', from: this.from, to: this.to}, {
                        preserveState: true,
                        preserveScroll: true,
                        replace: true
                    }
                )
            },

            readRateClass(rate) {
                const value = Number(rate || 0)

                if (value >= 80) {
                    return 'text-greendack-600'
                }

                if (value >= 50) {
                    return 'text-yellow-600'
                }

                return 'text-red-600'
            },

            formatNumber(value) {
                return new Intl.NumberFormat('fr-FR').format(Number(value || 0))
            },

            formatPercentage(value) {
                return `${Number(value || 0).toFixed(1)} %`
            },

            formatChartDate(value) {
                if (!value) {
                    return ''
                }

                return new Intl.DateTimeFormat(
                    'fr-FR',
                    {
                        day: '2-digit',
                        month: 'short'
                    }
                ).format(
                    new Date(`${value}T00:00:00`)
                )
            },

            typeIconClass(type) {
                const classes = {
                    announcement: 'bg-sky-100 text-sky-500',
                    information: 'bg-sky-100 text-sky-500',
                    promotion: 'bg-yellow-100 text-orange-600',
                    warning: 'bg-orange-100 text-yellow-500',
                    maintenance: 'bg-gray-100 text-gray-500',
                    update: 'bg-greendack-100 text-green-500'
                }

                return classes[type] || 'bg-gray-100 text-gray-500'
            },

            typeIcon(type) {
                const icons = {
                    announcement: 'fa fa-bullhorn',
                    information: 'fa fa-info-circle',
                    promotion: 'fa fa-tag',
                    warning: 'fa fa-exclamation-triangle',
                    maintenance: 'fa fa-wrench',
                    update: 'fa fa-refresh'
                }

                return icons[type] || 'fa fa-info-circle'
            },
        },
    })
</script>